import React, { useState, useCallback } from "react";
import { File } from "lucide-react";

const FILE_TYPES = {
  alternativeKMLFile: [".kml"], // Changed from alternativeKML
  justificationFile: [".pdf", ".doc", ".docx"],
  governmentOrderFile: [".pdf", ".doc", ".docx"],
  litigationDocuments: [".pdf", ".doc", ".docx"], // This stays the same
  violationDocuments: [".pdf", ".doc", ".docx"],
};

const OthersSection = ({
  formData: parentFormData,
  setFormData: setParentFormData,
}) => {
  const [files, setFiles] = useState({
    alternativeKMLFile: null, // Changed from alternativeKML
    justificationFile: null,
    governmentOrderFile: null,
    litigationDocuments: null,
    violationDocuments: null,
  });

  const [errors, setErrors] = useState({});

  // Use parent's OthersSection data
  const othersData = parentFormData.OtherSection || {};

  const validateFileUpload = useCallback(
    (file, fileType, maxSize = 5242880) => {
      if (!file) return "No file selected";

      console.log("File Type: ", fileType);
      const allowedTypes = FILE_TYPES[fileType];
      if (!allowedTypes) return "Invalid file type";

      const fileName = file.name.toLowerCase();
      const mimeType = file.type;

      const isValidType = allowedTypes.some((type) =>
        type.startsWith(".") ? fileName.endsWith(type) : mimeType === type
      );

      if (!isValidType) return "Invalid file type";
      if (file.size > maxSize) return "File size exceeds 5MB";
      return null;
    },
    []
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setParentFormData("OtherSection", { [name]: value });
    },
    [setParentFormData]
  );

  const handleFileUpload = useCallback(
    (e, fileType) => {
      const file = e.target.files[0];
      console.log("Uploading file for:", fileType, file); // Debug log

      const error = validateFileUpload(file, fileType);
      if (error) {
        console.log("Upload error:", error); // Debug log
        setErrors((prev) => ({ ...prev, [fileType]: error }));
        return;
      }

      // Update local state
      setFiles((prev) => {
        console.log("Updating files state:", fileType, file); // Debug log
        return {
          ...prev,
          [fileType]: file,
        };
      });

      // Update parent form data
      setParentFormData("OtherSection", {
        ...parentFormData.OtherSection,
        [fileType]: file,
      });

      setErrors((prev) => ({ ...prev, [fileType]: null }));
    },
    [validateFileUpload, setParentFormData, parentFormData]
  );

  const handleFileRemove = useCallback(
    (fileType) => {
      setFiles((prev) => ({
        ...prev,
        [fileType]: null,
      }));

      setParentFormData("OtherSection", {
        ...parentFormData.OtherSection,
        [fileType]: null,
      });

      setErrors((prev) => ({ ...prev, [fileType]: null }));
    },
    [setParentFormData, parentFormData]
  );

  const renderFileUpload = useCallback(
    ({ type, accept, label }) => (
      <div className="form-group upload-section">
        <label className="required">{label}</label>
        <div className="upload-container">
          <div className="upload-area">
            {!files[type] ? (
              <label className="file-input-label">
                <input
                  type="file"
                  accept={accept}
                  onChange={(e) => handleFileUpload(e, type)}
                  className="file-input"
                />
                <div className="upload-placeholder">
                  <File />
                  <p>Click to upload file</p>
                </div>
              </label>
            ) : (
              <div className="file-preview">
                <File />
                <span className="file-name">{files[type].name}</span>
                <button
                  type="button"
                  className="remove-file"
                  onClick={() => handleFileRemove(type)}
                >
                  ×
                </button>
              </div>
            )}
          </div>
          {errors[type] && <p className="error-message">{errors[type]}</p>}
        </div>
      </div>
    ),
    [files, errors, handleFileUpload, handleFileRemove]
  );

  return (
    <div className="form-section">
      <h3>Other Details</h3>
      <div className="form-group">
        <label className="required">
          Whether Rehabilitation and Resettlement (R&R) involved?
        </label>
        <select
          name="isRRInvolved"
          value={othersData.isRRInvolved || ""}
          onChange={handleInputChange}
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {othersData.isRRInvolved === "yes" && (
        <div className="rr-details">
          <div className="form-group">
            <label className="required">No. of Villages</label>
            <input
              type="number"
              name="numVillages"
              value={othersData.numVillages || ""}
              onChange={handleInputChange}
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label className="required">
              No. of Project Displaced Families
            </label>
            <input
              type="number"
              name="numDisplacedFamilies"
              value={othersData.numDisplacedFamilies || ""}
              onChange={handleInputChange}
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label className="required">No. of Project Affected Families</label>
            <input
              type="number"
              name="numAffectedFamilies"
              value={othersData.numAffectedFamilies || ""}
              onChange={handleInputChange}
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label className="required">
              Status of Rehabilitation & Resettlement
            </label>
            <select
              name="rehabilitationStatus"
              value={othersData.rehabilitationStatus || ""}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In-Progress</option>
              <option value="yet-to-start">Yet to Start</option>
            </select>
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="required">
          Whether project area involves shifting of
          watercourse/road/rail/transmission line/water pipeline, etc. required?
        </label>
        <select
          name="siteShifting"
          value={othersData.siteShifting || ""}
          onChange={handleInputChange}
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {othersData.siteShifting === "yes" && (
        <div className="site-shifting-details">
          <div className="form-group">
            <label className="required">Details</label>
            <textarea
              name="siteShiftingDetails"
              value={othersData.siteShiftingDetails || ""}
              onChange={handleInputChange}
              placeholder="Enter Details"
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="required">
          Whether any alternative site(s) examined or part thereof for the
          non-site-specific component?
        </label>
        <select
          name="alternativeSites"
          value={othersData.alternativeSites || ""}
          onChange={handleInputChange}
        >
          <option value="">Select</option>
          <option value="not-applicable">
            Not applicable as the project or activity is site-specific
          </option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {othersData.alternativeSites === "no" && (
        <div className="alternative-site-details">
          <div className="form-group">
            <label className="required">Details</label>
            <textarea
              name="alternativeSiteDetails"
              value={othersData.alternativeSiteDetails || ""}
              onChange={handleInputChange}
              placeholder="Enter Details"
            />
          </div>
        </div>
      )}
      {othersData.alternativeSites === "yes" && (
        <div className="alternative-site-details">
          {renderFileUpload({
            type: "alternativeKMLFile", // Changed from alternativeKML
            accept: ".kml",
            label: "Upload Alternative KML",
          })}
          <div className="form-group">
            <label className="required">Alternative site</label>
            <input
              type="text"
              name="alternativeSiteDetails"
              value={othersData.alternativeSiteDetails || ""}
              onChange={handleInputChange}
              placeholder="Enter Site"
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="required">
          Brief justification on selected site particularly highlighting
          environmental considerations
        </label>
        <textarea
          name="siteJustification"
          value={othersData.siteJustification || ""}
          onChange={handleInputChange}
          placeholder="Enter Justification"
        />
      </div>

      {renderFileUpload({
        type: "justificationFile",
        accept: ".pdf, .doc, .docx",
        label: "Upload copy of note containing justification",
      })}

      <div className="form-group">
        <label className="required">
          Whether there is any Government Order or Policy/ Court order relevant
          or restricting to the site?
        </label>
        <select
          name="governmentOrder"
          value={othersData.governmentOrder || ""}
          onChange={handleInputChange}
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {othersData.governmentOrder === "yes" && (
        <div className="government-order-details">
          <div className="form-group">
            <label className="required">Details of Order/Policy</label>
            <input
              type="text"
              name="governmentOrderDetails"
              value={othersData.governmentOrderDetails || ""}
              onChange={handleInputChange}
              placeholder="Details of Order/Policy"
            />
          </div>
          {renderFileUpload({
            type: "governmentOrderFile",
            accept: ".pdf, .doc, .docx",
            label: "Upload Government Order Documents",
          })}
        </div>
      )}

      <div className="form-group">
        <label className="required">
          Whether there is any litigation pending against the project and/or
          land in which the project is proposed to be set up?
        </label>
        <select
          name="pendingLitigation"
          value={othersData.pendingLitigation || ""}
          onChange={handleInputChange}
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {othersData.pendingLitigation === "yes" && (
        <div className="litigation-details">
          <div className="form-group">
            <label className="required">Name of the Court/Tribunal</label>
            <select
              name="courtName"
              value={othersData.courtName || ""}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              <option value="district courts">District Courts</option>
              <option value="high courts">High Courts</option>
              <option value="ngt">NGT</option>
              <option value="others"> Others {"(please specify)"}</option>
              <option value="supreme court of india">
                Supreme Court of India
              </option>
            </select>
          </div>
          <div className="form-group">
            <label className="required">Name of the specific Court/Bench</label>
            <input
              type="text"
              name="specificCourt"
              value={othersData.specificCourt || ""}
              onChange={handleInputChange}
              placeholder="Name of the specific Court/Bench"
            />
          </div>
          <div className="form-group">
            <label className="required">Case Category</label>
            <select
              name="caseCategory"
              value={othersData.caseCategory || ""}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              <option value="appeal">Appeal</option>
              <option value="ca/slp">CA/SLP {"(Cr)"}</option>
              <option value="complaint case">Complaint Case</option>
              <option value="oa">OA</option>
              <option value="others">Others {"(please specify)"}</option>
              <option value="slp(civil)">SLP {"(Civil)"}</option>
              <option value="wp(civil)">WP {"(Civil)"}</option>
              <option value="wp(Cr)">WP {"(Cr)"}</option>
            </select>
          </div>
          <div className="form-group">
            <label className="required">Status of court case</label>
            <select
              name="caseStatus"
              value={othersData.caseStatus || ""}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              <option value="disposed">Disposed</option>
              <option value="interim order">Interim Order</option>
              <option value="pending">Pending</option>
              <option value="reserved for judgment">
                Reserved for Judgment
              </option>
            </select>
          </div>
          <div className="form-group">
            <label className="required">
              Orders/Directions of the court, if any and its relevance with the
              proposed project
            </label>
            <input
              type="text"
              name="courtDirections"
              value={othersData.courtDirections || ""}
              onChange={handleInputChange}
              placeholder="Orders/Directions of the court"
            />
          </div>
          {renderFileUpload({
            type: "litigationDocuments",
            accept: ".pdf, .doc, .docx",
            label: "Upload Litigation Documents",
          })}
        </div>
      )}

      <div className="form-group">
        <label className="required">
          Whether the proposal involves violation of
          Act/Rule/Regulation/Notification of Central/State Government?
        </label>
        <select
          name="violationInvolved"
          value={othersData.violationInvolved || ""}
          onChange={handleInputChange}
        >
          <option value="">Select</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {othersData.violationInvolved === "yes" && (
        <div className="violation-details">
          <div className="form-group">
            <label className="required">
              Brief justification on selected site particularly highlighting
              environmental considerations
            </label>
            <textarea
              name="violationJustification"
              value={othersData.violationJustification || ""}
              onChange={handleInputChange}
              placeholder="Enter Justification"
            />
          </div>
          {renderFileUpload({
            type: "violationDocuments",
            accept: ".pdf, .doc, .docx",
            label: "Upload Violation Related Documents",
          })}
        </div>
      )}
    </div>
  );
};

export default React.memo(OthersSection);
